import debugFn from 'debug';
import { EventEmitter } from 'events';
import { MinimumSegment } from '../../../domain/segments';
import { Milliseconds, Timestamp } from '../../../types/date';
import { IPlayerDataProvider, MediaPlayer, PlayerEvent } from '../types';
import { AudioEngineFactory } from './engine/AudioEngineFactory';
import { IAudioEngine } from './engine/IAudioEngine';
import { PlaybackTech } from './types';
import { SegmentData, SegmentProvider } from './SegmentProvider';

/** Minimal engine factory interface used by the player; allows injecting test doubles. */
export interface SegmentsAudioPlayerImpl {
  AudioEngineFactory: {
    detectTech(): PlaybackTech;
    create(): Promise<IAudioEngine>;
  };
}

/**
 * Audio player that handles playback of segmented audio content.
 *
 * Audio is divided into segments (chunks of time), and this player handles:
 * - Fetching the correct segment for any given timestamp
 * - Seamless transitions between segments during continuous playback
 * - Prefetching upcoming segments to avoid playback gaps
 *
 * The player abstracts two different audio engines:
 * - MediaSourceEngine: Uses MSE API for gapless playback (modern browsers)
 * - AudioSrcEngine: Fallback using standard Audio element (Safari/older browsers)
 *
 * Time handling: The player works with absolute timestamps (Unix ms), while
 * engines work with segment-relative offsets. This class translates between both.
 *
 * @emits PlayerEvent.TS_UPDATE - Current playback timestamp (absolute)
 * @emits PlayerEvent.ERROR - Playback errors
 * @emits PlayerEvent.PLAYBACK_END - All audio finished
 * @emits PlayerEvent.NO_MORE_SEGMENTS - No more segments available
 */
export class SegmentsAudioPlayer extends EventEmitter implements MediaPlayer {
  /** Time before segment end to start prefetching next segment */
  private static readonly PREFETCH_THRESHOLD_MS = 10000;

  private debug: debugFn.Debugger;
  private segmentProvider: SegmentProvider;
  private enginePromise: Promise<IAudioEngine>;
  private engine: IAudioEngine | null = null;
  private currentSegment: MinimumSegment | null = null;
  private isDisposed = false;
  private hasNextSegmentPrefetched = false;
  /** Flag to ignore timeupdate events during seek operations */
  private isSeeking = false;

  /**
   * @param dataProvider - Provider that supplies segment data and audio URLs
   * @param playerId - Optional identifier for this player instance, used in debug logs
   * @param impl - Optional engine factory (default: real AudioEngineFactory); inject for tests.
   */
  constructor(
    dataProvider: IPlayerDataProvider,
    playerId?: string,
    impl: SegmentsAudioPlayerImpl = { AudioEngineFactory },
  ) {
    super();
    this.debug = debugFn(`SegmentsAudioPlayer-${playerId || 'default'}`);
    // Tech detection determines which engine and audio format to use
    const tech = impl.AudioEngineFactory.detectTech();
    this.segmentProvider = new SegmentProvider(dataProvider, tech);
    this.debug('initialized with tech: %s', tech);

    // Create engine immediately in constructor
    this.debug('creating engine');
    this.enginePromise = impl.AudioEngineFactory.create().then((engine) => {
      this.engine = engine;
      this.setupEngineEvents(engine);
      return engine;
    });
  }

  /** Starts or resumes audio playback. */
  async play(): Promise<void> {
    if (this.isDisposed) {
      throw new Error('Player is disposed');
    }

    this.debug('play');
    const engine = await this.enginePromise;
    await engine.play();
    this.emit(PlayerEvent.PLAYING);
  }

  /** Pauses audio playback. */
  pause(): void {
    if (this.isDisposed) return;
    this.debug('pause');
    this.engine?.pause();
    this.emit(PlayerEvent.PAUSED);
  }

  /**
   * Seeks to an absolute timestamp and starts playback from that position.
   *
   * The seek process:
   * 1. Fetches the segment containing the target timestamp
   * 2. Resets the engine (clears current buffers)
   * 3. Loads the new segment and seeks within it
   * 4. Begins prefetching the next segment
   * 5. Resumes playback
   *
   * @param args.ts - Absolute Unix timestamp in milliseconds to seek to
   * @param args.time - Ignored (used for video with playback ranges)
   * @param args.playAfterSeek - If false, only seek and do not start playback (e.g. cursor shift while paused).
   */
  async seek(args: { time: number; ts: number; playAfterSeek?: boolean }): Promise<void> {
    if (this.isDisposed) return;

    const targetTs = args.ts as Timestamp;
    this.debug('seek to ts=%d', targetTs);

    // Seeking flag prevents stale timeupdate events (from old position) from
    // firing during the async seek operation and confusing the UI
    this.isSeeking = true;
    this.emit(PlayerEvent.SEEKING_START);

    const engine = await this.enginePromise;
    engine.pause();

    let segmentData: SegmentData | null;
    try {
      segmentData = await this.segmentProvider.getSegmentAt(targetTs);
    } catch (error) {
      this.debug('error fetching segment at ts=%d: %s', targetTs, error);
      this.isSeeking = false;
      this.emit(PlayerEvent.SEEKING_END);
      this.emit(
        PlayerEvent.ERROR,
        new Error(
          `Failed to fetch segment: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
      return;
    }

    if (!segmentData) {
      this.debug('no segment found for ts=%d', targetTs);
      this.isSeeking = false;
      this.emit(PlayerEvent.SEEKING_END);
      this.emit(PlayerEvent.ERROR, new Error('No segment found for timestamp'));
      return;
    }

    // Reset clears all buffered data - necessary when jumping to a different segment
    await engine.reset();
    await engine.enqueue(segmentData);

    this.currentSegment = segmentData.segment;
    this.hasNextSegmentPrefetched = false;

    // Convert absolute timestamp to segment-relative offset for engine
    // Example: target=1000050, segmentStart=1000000 → offset=50ms into segment
    const targetOffsetMs = (targetTs - segmentData.segment.startTs) as Milliseconds;
    this.debug('seeking within segment, offset=%dms', targetOffsetMs);

    // Engine may return different position than requested (e.g., clamped to audio duration)
    const actualOffsetMs = await engine.seekToOffset(targetOffsetMs);

    // Convert back to absolute timestamp for UI
    const actualTs = segmentData.segment.startTs + actualOffsetMs;
    this.emit(PlayerEvent.TS_UPDATE, actualTs);

    this.prefetchNext();
    this.isSeeking = false;
    this.emit(PlayerEvent.SEEKING_END);

    const shouldPlay = args.playAfterSeek !== false;
    if (shouldPlay) {
      await engine.play();
      // Emit PLAYING so the store's onPlayerPlaying runs (playback started via seek path).
      this.emit(PlayerEvent.PLAYING);
    }
  }

  /**
   * Wires up event handlers from engine to player events.
   * Translates engine's segment-relative times to absolute timestamps.
   */
  private setupEngineEvents(engine: IAudioEngine): void {
    // Engine emits time as offset within current segment (0 to segmentDuration)
    engine.on('timeupdate', (offsetMs: number) => {
      if (this.isSeeking) return;
      if (!this.currentSegment) return;

      // Translate to absolute: segmentStart + offset = absolute timestamp
      const absoluteTs = this.currentSegment.startTs + offsetMs;
      this.emit(PlayerEvent.TS_UPDATE, absoluteTs);

      this.checkPrefetch(offsetMs);
    });

    // Segment ended = current segment's audio finished, need to switch to next
    engine.on('segmentEnded', () => {
      this.debug('segment ended, advancing to next');
      this.advanceToNextSegment();
    });

    engine.on('error', (error: Error) => {
      this.debug('engine error: %s', error.message);
      this.emit(PlayerEvent.ERROR, error);
    });

    // Ended = all queued audio finished (no more segments in buffer)
    engine.on('ended', () => {
      this.debug('engine playback ended');
      this.emit(PlayerEvent.PLAYBACK_END);
    });
  }

  /**
   * Checks if playback is approaching segment end and triggers prefetch.
   * Called on every timeupdate to ensure smooth transitions.
   */
  private checkPrefetch(currentOffsetMs: number): void {
    if (!this.currentSegment) return;

    const segmentDuration = this.currentSegment.endTs - this.currentSegment.startTs;
    const remainingMs = segmentDuration - currentOffsetMs;

    const shouldPrefetch =
      remainingMs < SegmentsAudioPlayer.PREFETCH_THRESHOLD_MS &&
      !this.segmentProvider.isPrefetching() &&
      !this.hasNextSegmentPrefetched;

    if (shouldPrefetch) {
      this.prefetchNext();
    }
  }

  /**
   * Fetches and enqueues the next segment into the engine's buffer.
   * This enables gapless playback - next segment is ready before current ends.
   * Errors during prefetch are logged but don't break playback (best effort).
   */
  private async prefetchNext(): Promise<void> {
    if (!this.currentSegment) return;

    this.debug('prefetching next segment');

    try {
      const nextData = await this.segmentProvider.getNextSegment(this.currentSegment.endTs);

      if (nextData) {
        const engine = this.engine || (await this.enginePromise);
        await engine.enqueueNext(nextData);
        this.hasNextSegmentPrefetched = true;
      }
    } catch (error) {
      // Prefetch failures are non-fatal - log but don't break playback
      this.debug('prefetch failed: %s', error);
      this.emit(
        PlayerEvent.ERROR,
        new Error(
          `Failed to prefetch next segment: ${error instanceof Error ? error.message : String(error)}`,
        ),
      );
    }
  }

  /**
   * Transitions playback state to the next segment.
   * Called when engine signals current segment finished.
   * Updates currentSegment reference and notifies engine to switch buffers.
   */
  private advanceToNextSegment(): void {
    if (!this.currentSegment) return;

    this.debug('advancing to next segment');

    const nextSegment = this.segmentProvider.advanceSegment();

    if (!nextSegment) {
      this.debug('no more segments');
      this.emit(PlayerEvent.NO_MORE_SEGMENTS);
      return;
    }

    const nextSegmentDuration = nextSegment.endTs - nextSegment.startTs;
    this.currentSegment = nextSegment;
    this.hasNextSegmentPrefetched = false;

    // Engine needs duration to correctly calculate segment-relative offsets
    this.engine?.advanceToNextBuffer(nextSegmentDuration);
  }

  /**
   * Releases all resources. Player cannot be used after calling dispose.
   * Should be called when the player is no longer needed to prevent memory leaks.
   */
  dispose(): void {
    if (this.isDisposed) return;

    this.debug('dispose');
    this.isDisposed = true;
    this.engine?.dispose();
    this.engine = null;
    this.currentSegment = null;
    this.removeAllListeners();
  }

  mute(): void {
    this.engine?.mute();
  }

  unmute(): void {
    this.engine?.unmute();
  }

  isMuted(): boolean {
    return this.engine?.isMuted() ?? false;
  }

  /** Sets playback volume on the active audio engine using a 0-100 scale. */
  setVolume(value: number): void {
    if (this.isDisposed) return;

    if (this.engine) {
      this.engine.setVolume(value);
      return;
    }

    void this.enginePromise.then((engine) => {
      if (!this.isDisposed) {
        engine.setVolume(value);
      }
    });
  }
}
